import { gql } from '@apollo/client';
import client from '../apollo-client';

export interface NotificationUser {
  id: number;
  email: string;
  name: string;
}

export interface Notification {
  id: number;
  userId: number;
  type: 'reminder' | 'system' | 'announcement' | 'alert';
  title: string | null;
  message: string | null;
  related_entity_type: string | null;
  related_entity_id: number | null;
  channel: 'email' | 'push' | 'sms' | 'in_app';
  status: 'pending' | 'sent' | 'failed' | 'read';
  sent_at: string | null;
  read_at: string | null;
  error_message: string | null;
  metadata: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  user?: NotificationUser;
}

export class NotificationService {
  /**
   * Obține toate notificările utilizatorului autentificat
   * Sortate descrescător după data creării (cele mai noi primele)
   */
  async getNotifications(): Promise<Notification[]> {
    const response = await client.query({
      query: gql`
        query GetNotifications {
          notifications {
            id
            userId
            type
            title
            message
            related_entity_type
            related_entity_id
            channel
            status
            sent_at
            read_at
            createdAt
            updatedAt
            user {
              id
              email
              name
            }
          }
        }
      `,
      fetchPolicy: 'network-only',
    });
    return response.data.notifications;
  }

  /**
   * Obține o notificare specifică după ID
   */
  async getNotification(id: number): Promise<Notification> {
    const response = await client.query({
      query: gql`
        query GetNotification($id: Int!) {
          notification(id: $id) {
            id
            userId
            type
            title
            message
            related_entity_type
            related_entity_id
            channel
            status
            sent_at
            read_at
            error_message
            metadata
            createdAt
            updatedAt
          }
        }
      `,
      variables: { id },
      fetchPolicy: 'network-only',
    });
    return response.data.notification;
  }

  /**
   * Marchează o notificare ca fiind citită
   * Schimbă status din 'sent' în 'read' și setează read_at
   */
  async markNotificationAsRead(id: number): Promise<Notification> {
    const response = await client.mutate({
      mutation: gql`
        mutation MarkNotificationAsRead($id: Int!) {
          markNotificationAsRead(id: $id) {
            id
            status
            read_at
          }
        }
      `,
      variables: { id },
    });
    return response.data.markNotificationAsRead;
  }

  /**
   * Marchează toate notificările necitite ca fiind citite
   * Returnează true dacă operațiunea a avut succes
   */
  async markAllNotificationsAsRead(): Promise<boolean> {
    const response = await client.mutate({
      mutation: gql`
        mutation MarkAllNotificationsAsRead {
          markAllNotificationsAsRead
        }
      `,
    });
    return response.data.markAllNotificationsAsRead;
  }

  /**
   * Șterge o notificare (soft delete)
   * Returnează true dacă ștergerea a avut succes
   */
  async deleteNotification(id: number): Promise<boolean> {
    const response = await client.mutate({
      mutation: gql`
        mutation DeleteNotification($id: Int!) {
          deleteNotification(id: $id)
        }
      `,
      variables: { id },
    });
    return response.data.deleteNotification;
  }

  /**
   * Helper pentru a obține doar notificările necitite
   */
  async getUnreadNotifications(): Promise<Notification[]> {
    const notifications = await this.getNotifications();
    return notifications.filter(n => n.status !== 'read');
  }

  /**
   * Helper pentru a obține numărul de notificări necitite
   */
  async getUnreadCount(): Promise<number> {
    const notifications = await this.getNotifications();
    return notifications.filter(n => n.status !== 'read').length;
  }
}
